import { AnimatePresence } from 'framer-motion';
import React, { useState } from 'react';

import RichText from 'components/RichText/RichText';
import {
  FaqType,
  JoinUsPageType,
  OpportunitiesData,
} from 'containers/JoinUsPage/JoinUsPage.types';
import useOverElement from 'hooks/useOverElement';
import { useOpportunitiesStore } from 'store';
import { MediaCMS } from 'utils/sharedStrapiQueries/sharedTypes';

import * as S from './JoinUsInternship.styles';

export interface JoinUsInternshipProps {
  internshipTitle: string;
  internshipDescription: string;
  ctaLabel: string;
  internshipTypes: Array<{
    title;
    description;
    image: MediaCMS;
    titleHoverColor: string;
    titleDefaultHoverColor: string;
  }>;
  opportunitiesData: OpportunitiesData;
  faq: Array<FaqType>;
  pageData: JoinUsPageType;
}

const defaultProps: Partial<JoinUsInternshipProps> = {};

const JoinUsInternship: React.FunctionComponent<
  JoinUsInternshipProps
> = props => {
  const [openIndex, setOpenIndex] = useState(0);
  const { openOpportunityList } = useOpportunitiesStore();

  const [ref] = useOverElement();

  return (
    <S.JoinUsInternshiOverlayWrapper ref={ref}>
      <S.Header>
        <S.Title>
          <RichText
            as="h3"
            size="heading3"
            richText={props.internshipTitle}
            defaultFormatting
          />
        </S.Title>
        <S.Description>
          <RichText
            as="p"
            size="smallBody"
            richText={props.internshipDescription}
            defaultFormatting
          />
        </S.Description>
      </S.Header>
      <S.TypesWrapper>
        {props.internshipTypes.map((type, index) => (
          <Accordion
            key={type.title}
            isOpen={openIndex === index}
            onClick={() => {
              setOpenIndex(index === openIndex ? null : index);
            }}
            titleHoverColor={type?.titleHoverColor}
            titleDefaultHoverColors={type?.titleDefaultHoverColor}
            {...type}
          />
        ))}
        <S.ButtonWrapper>
          <S.Button label={props.ctaLabel} onClick={openOpportunityList} />
        </S.ButtonWrapper>
      </S.TypesWrapper>
      {openIndex !== null && (
        <S.IntenshipTypeImage
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          src={props.internshipTypes[openIndex].image.url}
        />
      )}
    </S.JoinUsInternshiOverlayWrapper>
  );
};

const Accordion = ({
  title,
  description,
  image,
  isOpen,
  onClick,
  titleHoverColor,
  titleDefaultHoverColors,
}) => {
  return (
    <>
      <S.AccordionHeader
        isOpen={isOpen}
        onClick={onClick}
        titleColors={titleHoverColor || titleDefaultHoverColors}
      >
        <RichText as="p" size="heading5" richText={title} defaultFormatting />

        <AnimatePresence initial={false}>
          {isOpen && (
            <S.AccordionItem
              key="content"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { height: 'auto' },
                collapsed: { height: 0 },
              }}
              transition={{ duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98] }}
            >
              <div>
                <RichText
                  as="p"
                  size="smallBody"
                  richText={description}
                  defaultFormatting
                />
                <img src={image.url} />
              </div>
            </S.AccordionItem>
          )}
        </AnimatePresence>
      </S.AccordionHeader>
    </>
  );
};

JoinUsInternship.defaultProps = defaultProps;

export default JoinUsInternship;
